// ------------ import external dependencies -------------
import { useState } from "react";
import { Link } from "react-router-dom";

import { useNavigate } from "react-router-dom";

// ------------- import internal dependencies ------------
import PersonalData from "./components/PersonalData";
import Roles from "./components/Roles";
import UserEmail from "./components/UserEmail";
import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import { useStoreWrapper } from "../../../store/store";
import { getProvisioningStatus } from "../../../api/authApi";
import {
  blockedAddUserMessage,
  canEditOwnEmail,
  hasEmail,
} from "./actingUserEmail";
// import useUser from "../hooks/useUser";
// ------ types ------
import { Stages } from "./type";
// ------ styles -------
import {
  BackArrowIon,
  FormWrapper,
  Heading,
  IconHeadingContainer,
  MainInputContainer,
} from "./createUser.style";
// ------ assets ------
import CheckImg from "../../../assets/images/check.png";
import logoLight from "../../../assets/images/mG_logo_light.svg";
import "../../../components/SuspenseFallback/style.css";

function CreateUser() {
  // @ts-ignore
  const state = window.history?.state?.usr || null;

  const user = state?.userInfo || {};
  // ------- intialize component custom hooks -------
  const navigate = useNavigate();
  const resetUser = useStoreWrapper((state) => state.resetUser);
  const level = useStoreWrapper((state) => state.stage);
  const updateStage = useStoreWrapper((state) => state.updateStage);
  const edit = useStoreWrapper((state) => state.edit);

  const newUserEmail = useStoreWrapper((state) => state.email);

  const actingUser = useStoreWrapper((state) => state.user);

  // null until a user is created, true once the sign-in exists, false when setup failed.
  const [provisioned, setProvisioned] = useState<boolean | null>(null);

  // We stopped polling without an answer. Not a failure: the sign-in is usually just slow.
  const [stillSettingUp, setStillSettingUp] = useState(false);

  const [reportSentTo, setReportSentTo] = useState<string | null>(null);

  const [newContactId, setNewContactId] = useState<string | null>(null);

  // ----- aplication state managers -------
  const [stage, setStage] = useState(level);
  const [open, setOpen] = useState(false);

  const stages: Stages = {
    1: {
      title: "Enter User’s  Personal data",
      completed: false,
    },
    2: {
      title: "Assign User Roles and Permissions",
      completed: false,
    },
    3: {
      title: "Enter User Email",
      completed: false,
    },
  };

  /**
   * Create new user account
   */
  const createNewUser = (obj: any) => {
    switch (stage) {
      case 1:
        setStage(2);
        updateStage();
        break;
      case 2:
        setStage(3);
        updateStage();
        break;
      case 3:
        if (obj) {
          setProvisioned(null);
          setStillSettingUp(false);
          setOpen(true);

          if (typeof obj === "string") {
            setNewContactId(obj);
            waitForProvisioning(obj);
          }
        }
        break;
      default:
        break;
    }
  };

  /*
   * Polls until the new user's sign-in exists. Giving up is not failure: it only means
   * provisioning has not finished yet, so the message must not tell the user it failed,
   * or they may create a duplicate.
   */
  const waitForProvisioning = async (contactId: string) => {
    const delays = [1000, 1500, 2000, 3000, 4000, 5000, 6000];

    for (const delay of delays) {
      await new Promise((resolve) => setTimeout(resolve, delay));

      try {
        const { data } = await getProvisioningStatus(contactId);

        if (data?.provisioned) {
          setReportSentTo(data?.reportSentTo ?? null);
          setProvisioned(true);
          return;
        }

        if (data?.failed) {
          setProvisioned(false);
          return;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("Could not read provisioning status, will retry", error);
      }
    }

    setStillSettingUp(true);
  };

  const back = () => {
    if (edit) {
      navigate(-1);
      return;
    }

    if (stage === 1) {
      navigate("/user/users");
    } else {
      setStage(stage - 1);
    }
  };

  // Direct URL visits skip the list-screen button, so the wizard guards itself too.
  if (!hasEmail(actingUser)) {
    return (
      <section>
        <h1 className="text-2xl font-bold">Add New User</h1>
        <FormWrapper>
          <p className="text-base">{blockedAddUserMessage(actingUser)}</p>
          <div className="mt-4 flex gap-6">
            {canEditOwnEmail(actingUser) && (
              <Link
                to="/user/settings"
                className="font-bold"
                style={{ color: "var(--primaryPurple)" }}
              >
                Go to my profile
              </Link>
            )}
            <Link
              to="/user/users"
              className="font-bold"
              style={{ color: "var(--primaryPurple)" }}
            >
              Back to user list
            </Link>
          </div>
        </FormWrapper>
      </section>
    );
  }

  const waiting = newContactId !== null && provisioned === null && !stillSettingUp;

  // -------- Personal data form configuration -------

  return (
    <section>
      <h1 className="text-2xl font-bold">
        {edit ? "Edit User" : "Add New User"}
      </h1>

      {/* ------- user creation stage ------- */}
      <FormWrapper>
        <IconHeadingContainer>
          <BackArrowIon onClick={back} />
          <Heading>{stages[stage].title}</Heading>
        </IconHeadingContainer>
        {/* ------- form handler for user creation stage ------- */}
        <MainInputContainer>
          {/* ------ form stages ------- */}
          {stage === 1 && (
            <PersonalData cb={createNewUser} editUserData={user} />
          )}
          {stage === 2 && <Roles cb={createNewUser} />}
          {stage === 3 && <UserEmail cb={createNewUser} />}
        </MainInputContainer>
      </FormWrapper>

      {/* ------ success modal ------- */}
      <Modal open={open}>
        <div>
          {waiting ? (
            <>
              <img
                src={logoLight}
                alt="Mitigate Risk logo"
                className="fallback__logo mx-auto block h-16 w-16"
              />
              <span className="mx-auto block max-w-[440px] pt-4 text-center font-inter text-sm text-white-main opacity-90">
                {newUserEmail
                  ? "Sending invite email..."
                  : "Sending temporary password email..."}
              </span>
            </>
          ) : (
            <>
              <img
                src={CheckImg}
                alt="User Success"
                className="mx-auto block"
              />
              <span className="block pt-4 text-center font-inter text-2xl font-semibold text-white-main">
                User successfully added
              </span>
            </>
          )}

          <span className="mx-auto block max-w-[440px] pt-3 text-center font-inter text-sm text-white-main opacity-90">
            {provisioned === null && !waiting && !stillSettingUp && "User created."}

            {stillSettingUp &&
              (newUserEmail
                ? `Their sign-in is still being set up. ${newUserEmail} will get a link to set their password shortly. No need to add them again.`
                : "Their sign-in is still being set up. You'll get their temporary password by email shortly. No need to add them again.")}

            {provisioned === true &&
              (newUserEmail
                ? `We've emailed ${newUserEmail} a link to set their password.`
                : `Temporary password emailed to you at ${
                    reportSentTo ?? actingUser?.email ?? "your email address"
                  }. It expires in 7 days.`)}

            {provisioned === false &&
              "Something went wrong setting up their sign-in."}
          </span>
          {provisioned === false && (
            <span className="mx-auto block max-w-[420px] pt-3 text-center font-inter text-xs text-white-main opacity-75">
              Contact your administrator to finish setting up their sign-in.
            </span>
          )}

          {!waiting && (
            <>
              <Button
                textContent="Add New User"
                variant="md"
                addClass=" bg-white-main text-primary-main font-medium mt-5 block mx-auto w-[350px]"
                click={() => {
                  setStage(1);
                  resetUser();
                  setOpen(false);
                  setProvisioned(null);
                  setStillSettingUp(false);
                  setReportSentTo(null);
                  setNewContactId(null);
                }}
              />
              <Link
                to="/user/users"
                className="block pt-5 text-center text-white-main"
                onClick={() => {
                  resetUser();
                  setOpen(false);
                  setStage(1);
                }}
              >
                Go back to user list
              </Link>
            </>
          )}
        </div>
      </Modal>
    </section>
  );
}

export default CreateUser;
